import React from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useMediaQuery,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import { motion } from "framer-motion";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { t } from "i18next";

function DataTable({
  columns = [],
  data = [],
  getImage,
  getRowKey,
  renderRowActions,
  emptyMessage = "No data available",
  page,
  rowsPerPage,
  handlePageChange,
  handleRowsPerPageChange,
}) {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const tableRef = React.useRef(null);
  const [hoveredRow, setHoveredRow] = React.useState(null);
  const [mobileMenuAnchor, setMobileMenuAnchor] = React.useState(null);
  const [menuRowKey, setMenuRowKey] = React.useState(null);

  // helper to flatten out any React.Fragment wrappers
  const flattenChildren = (children) =>
    React.Children.toArray(children).flatMap((child) =>
      React.isValidElement(child) && child.type === React.Fragment
        ? flattenChildren(child.props.children)
        : [child]
    );

  const openMobileMenu = (event, rowKey) => {
    setMobileMenuAnchor(event.currentTarget);
    setMenuRowKey(rowKey);
  };
  const closeMobileMenu = () => {
    setMobileMenuAnchor(null);
    setMenuRowKey(null);
  };

  const paginatedData = data.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // On mobile we want to show all items since there is no pagination
  const displayedData = isMobile ? data : paginatedData;

  const scrollTableToTop = () => {
    if (!tableRef.current) return;
    const top =
      tableRef.current.getBoundingClientRect().top + window.pageYOffset;
    window.scrollTo({ top, behavior: "smooth" });
  };
  const onChangePageAndScroll = (event, newPage) => {
    handlePageChange(event, newPage);
    scrollTableToTop();
  };
  const onRowsPerPageChangeAndScroll = (event) => {
    handleRowsPerPageChange(event);
    scrollTableToTop();
  };

  return (
    <motion.div
      key={page}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}>
      {isMobile ? (
        <Box className='flex flex-col gap-3 w-full px-3 py-2'>
          {displayedData.map((row) => {
            // flatten out any <Fragment> wrappers from renderRowActions:
            const actions = renderRowActions
              ? flattenChildren(renderRowActions(row))
              : [];

            return (
              <motion.div
                key={getRowKey(row)}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ scale: 1.01, backgroundColor: "#f5f5f5" }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                className='border rounded-lg p-4 shadow-sm bg-white w-full'>
                {getImage && (
                  <Box className='mb-3 flex justify-center'>
                    <img
                      src={getImage(row)}
                      alt='thumbnail'
                      className='w-28 h-28 object-cover rounded-lg'
                    />
                  </Box>
                )}

                <Box className='flex flex-col divide-y divide-gray-200'>
                  {columns
                    .filter((col) => col.mobile)
                    .map((col) => (
                      <Box
                        key={col.header}
                        className='flex justify-between items-center py-1'>
                        <span className='font-semibold text-sm text-gray-700'>
                          {col.header}:
                        </span>
                        <span className='text-sm'>{col.accessor(row)}</span>
                      </Box>
                    ))}
                </Box>

                {actions.length > 0 && (
                  <Box className='flex justify-end mt-2'>
                    <IconButton
                      onClick={(e) => openMobileMenu(e, getRowKey(row))}>
                      <MoreVertIcon />
                    </IconButton>
                    <Menu
                      anchorEl={mobileMenuAnchor}
                      open={menuRowKey === getRowKey(row)}
                      onClose={closeMobileMenu}
                      keepMounted
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                      }}>
                      {actions.map((actionEl, idx) => {
                        const button =
                          actionEl.type === Tooltip
                            ? React.Children.toArray(
                                actionEl.props.children
                              ).find((c) => c.type === IconButton)
                            : actionEl.type === IconButton
                            ? actionEl
                            : null;

                        if (!button) return null;

                        const label =
                          actionEl.type === Tooltip
                            ? actionEl.props.title
                            : button.props["aria-label"] || "";

                        return (
                          <MenuItem
                            key={idx}
                            disabled={button.props.disabled}
                            onClick={() => {
                              button.props.onClick?.();
                              closeMobileMenu();
                            }}>
                            <ListItemIcon>{button.props.children}</ListItemIcon>
                            <ListItemText primary={label} />
                          </MenuItem>
                        );
                      })}
                    </Menu>
                  </Box>
                )}
              </motion.div>
            );
          })}
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          ref={tableRef}
          sx={{ mt: 4, width: "100%", boxShadow: 3 }}>
          <Table
            size='medium'
            stickyHeader>
            <TableHead>
              <TableRow>
                {getImage && (
                  <TableCell>
                    <strong>{t("tables:image")}</strong>
                  </TableCell>
                )}
                {columns.map((col) => (
                  <TableCell
                    key={col.header}
                    align={col.align || "left"}>
                    <strong>{col.header}</strong>
                  </TableCell>
                ))}
                {renderRowActions && (
                  <TableCell>
                    <strong>{t("tables:actions")}</strong>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((row) => (
                <motion.tr
                  key={getRowKey(row)}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  onMouseEnter={() => setHoveredRow(getRowKey(row))}
                  onMouseLeave={() => setHoveredRow(null)}
                  style={{
                    backgroundColor:
                      hoveredRow === getRowKey(row) ? "#f9f9f9" : "transparent",
                  }}>
                  {getImage && (
                    <TableCell>
                      <Box
                        sx={{
                          width: 75,
                          height: 60,
                          overflow: "hidden",
                          borderRadius: 2,
                        }}>
                        <img
                          src={getImage(row)}
                          alt='thumbnail'
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            borderRadius: 8,
                          }}
                        />
                      </Box>
                    </TableCell>
                  )}
                  {columns.map((col) => (
                    <TableCell
                      key={col.header}
                      align={col.align || "left"}>
                      {col.accessor(row)}
                    </TableCell>
                  ))}
                  {renderRowActions && renderRowActions(row) && (
                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.5,
                        }}>
                        {renderRowActions(row)}
                      </Box>
                    </TableCell>
                  )}
                </motion.tr>
              ))}
            </TableBody>
          </Table>
          <Box
            sx={{
              width: "100%",
              px: 1,
              py: 0.5,
              display: "flex",
              justifyContent: "center",
            }}>
            <TablePagination
              component='div'
              count={data.length}
              page={page}
              onPageChange={onChangePageAndScroll}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={onRowsPerPageChangeAndScroll}
              rowsPerPageOptions={isMobile ? [rowsPerPage] : [5, 10, 25]}
              labelRowsPerPage={isMobile ? "" : undefined}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} of ${count}`
              }
            />
          </Box>
        </TableContainer>
      )}

      {data.length === 0 && (
        <Typography
          variant='h6'
          className='text-gray-500 text-center my-10'>
          {emptyMessage}
        </Typography>
      )}
    </motion.div>
  );
}

export default DataTable;
